import { Link } from "react-router-dom";

/**
 * Public biometric retention + use policy page (/biometric-policy).
 *
 * Linked from the PlaidIDVConsentDialog. Required by BIPA §15(a) which
 * mandates a publicly available written policy describing retention
 * schedule and destruction guidelines for biometric data.
 *
 * Status: STUB — this is the launch-option-C minimum (per
 * docs/security/PLAID_IDV_COMPLIANCE_AUDIT.md §7.1). A lawyer-reviewed
 * version replaces this before the prod IDV flip per the §8 checklist.
 */
export default function BiometricPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-4 py-8 space-y-6">
        <div>
          <Link to="/" className="text-xs text-muted-foreground hover:underline">
            ← Back
          </Link>
        </div>
        <header>
          <h1 className="text-2xl font-bold text-foreground">Biometric Retention Policy</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Last updated: 2026-09-23
          </p>
        </header>

        <section className="space-y-3 text-sm leading-relaxed text-foreground">
          <h2 className="text-lg font-semibold">What this covers</h2>
          <p>
            This policy describes how DigaVouch handles biometric information
            collected during identity verification (IDV). It applies to anyone
            using DigaVouch's voucher or applicant flows where IDV is offered.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-foreground">
          <h2 className="text-lg font-semibold">What is collected, and by whom</h2>
          <p>
            Identity verification on DigaVouch is performed by our partner{" "}
            <span className="font-semibold">Plaid Inc.</span> ("Plaid").
            When you complete IDV, Plaid collects:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>A photograph of a government-issued ID you submit</li>
            <li>A live selfie you submit</li>
            <li>
              A <span className="font-semibold">facial-recognition template</span>{" "}
              derived from the selfie, which Plaid compares against the photo on
              your ID document
            </li>
          </ul>
          <p>
            <span className="font-semibold">Plaid holds this biometric data.</span>{" "}
            DigaVouch does not receive the document images, the selfie image, or
            the facial-recognition template.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-foreground">
          <h2 className="text-lg font-semibold">What DigaVouch keeps</h2>
          <p>
            DigaVouch receives and stores only:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>The verification <span className="font-semibold">outcome</span> (verified or not verified)</li>
            <li>A Plaid session identifier so we can look up the record on Plaid's side if needed</li>
            <li>The name Plaid verified against your ID</li>
            <li>Summaries of how each verification field compared (for example, whether your name matched)</li>
          </ul>
          <p>
            DigaVouch does <span className="font-semibold">not</span> store the
            facial-recognition template, document images, or selfie images.
            DigaVouch does <span className="font-semibold">not</span> use
            biometric information to identify you outside of the single
            verification session that produced it.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-foreground">
          <h2 className="text-lg font-semibold">Retention and deletion</h2>
          <p>
            DigaVouch has not yet finished building an automatic deletion
            schedule for the records described above. Until that schedule is
            in place, we delete your verification outcome and the related
            information above on request rather than automatically. You can
            ask us to delete it at any time (see "Your rights" below), and we
            will do so.
          </p>
          <p>
            Plaid's retention of the underlying biometric data is governed by
            Plaid's own privacy policy and our data-processing agreement with
            Plaid. We will request deletion of biometric data held by Plaid
            when you ask us to (see "Your rights" below).
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-foreground">
          <h2 className="text-lg font-semibold">Use and disclosure</h2>
          <p>
            The verification outcome is used by the organization you applied
            with (or are vouching for) as one of several inputs into their
            review. We do <span className="font-semibold">not</span> sell, lease,
            or trade biometric information. We do not disclose biometric data
            to any party other than Plaid (our processor) without your separate
            written consent.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-foreground">
          <h2 className="text-lg font-semibold">Your rights</h2>
          <p>
            You can:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Ask whether DigaVouch holds a verification outcome for you</li>
            <li>Ask for that record to be deleted</li>
            <li>Ask DigaVouch to request that Plaid delete the underlying biometric data on your behalf</li>
            <li>Withdraw your consent at any time (this prevents future processing; it does not retroactively undo a verification you have already completed)</li>
          </ul>
          <p>
            Send any of these requests to{" "}
            <a href="mailto:privacy@digavouch.com" className="underline font-semibold">
              privacy@digavouch.com
            </a>
            . We aim to respond within 30 days.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-foreground">
          <h2 className="text-lg font-semibold">Storage and security</h2>
          <p>
            The verification outcome is stored in DigaVouch's production
            databases hosted in the United States, with access restricted to
            personnel who need it for operating DigaVouch. Plaid's storage,
            including biometric data, is governed by Plaid's security program
            (SOC 2 Type II reported).
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-foreground">
          <h2 className="text-lg font-semibold">Changes to this policy</h2>
          <p>
            We may update this policy as our practices change or as required by
            law. We will update the "Last updated" date above. Material changes
            will be communicated through the application flow.
          </p>
        </section>

        <p className="text-xs text-muted-foreground pt-4 border-t border-border">
          DigaVouch — privacy@digavouch.com
        </p>
      </div>
    </div>
  );
}
